import { FormData } from "@/getData/getIndexedFormsData.ts";
import { useCallback, useEffect, useRef, useState } from "react";
import { generateSchema } from "./generateSchema";
import { genFieldName, renderFields } from "./renderFields";
import { useRouter } from "next/router";
import { createFormStructure } from "./createFormStructure";
import { flattenFormStructure } from "./flattenFormStructure";
import { isFieldVisible } from "./isFieldVisible";
import { FormMessages } from "./FormMessage";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { FormErrorState } from "./Inputs/Inputs";
import { safeParseSchema } from "@/utils/validations";
import { Header_Forms } from "@/wp/types/acf-header-types";
import { FormsRequest } from "@/pages/api/forms";
import { Modul_Formbuilder } from "@/wp/types/acf-module-types";
import { Loader } from "@/components/Loader/Loader";
import { has } from "lodash";
import styles from "./Form.module.scss";
import { SimpleButton } from "@/components/Button/Button";
import { optimizeURL } from "@/utils/url.ts";
import { useAppContext } from "@/context/AppContext.tsx";

type NonFalse<T> = T extends false ? never : T;

export type ACFFormElement = NonFalse<
  Header_Forms["form"]["input_fields"]
>[number];

export type FormBuilderType = Modul_Formbuilder;

interface FormProps {
  formData: FormData;
  friendlyCaptchaApplicationkey: string;
  button_type: "primary" | "secondary";
}

export type FormValuesType = Record<
  string,
  string | boolean | number | string[]
>;
export type FormState = "init" | "loading" | "success" | "error";

const paidPostOrderButtonLabel = "Kostenpflichtig bestellen";

const normalizeFormOption = (value: string | false | undefined) =>
  typeof value === "string"
    ? value.toLocaleLowerCase().replace(/[\s-]/g, "")
    : "";

const isPerPostOption = (value: string | false | undefined) =>
  normalizeFormOption(value).includes("perpost");

const isPerPostSelected = (
  inputFields: ACFFormElement[],
  formValues: FormValuesType,
) => {
  return inputFields.some((field) => {
    if (field.input_type !== "radiobutton" || !field.radio_inputs) {
      return false;
    }

    const name = field.feld_name || genFieldName(field.fieldlabel);
    const selectedValue = formValues[name];
    if (selectedValue === undefined) return false;

    const selectedIndex = Number(selectedValue);
    const selectedOption = Number.isNaN(selectedIndex)
      ? field.radio_inputs.find(
          (option) =>
            option.value === selectedValue ||
            option.fieldlabel === selectedValue,
        )
      : field.radio_inputs[selectedIndex];

    return (
      isPerPostOption(selectedOption?.fieldlabel) ||
      isPerPostOption(selectedOption?.value)
    );
  });
};

const Form = ({
  formData,
  friendlyCaptchaApplicationkey,
  button_type,
}: FormProps) => {
  const { getLabel } = useGlobalContext();
  const { isNobleElements } = useAppContext();

  const inputFields = formData.formHeader.form.input_fields || [];
  const [formValues, setFormValues] = useState<FormValuesType>({});
  const [errors, setErrors] = useState<FormErrorState>({});
  const [captchaSolution, setCaptchaSolution] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>("init");
  const friendlyCaptchaApplicationKey = formData.formHeader.form
    .friendly_captcha
    ? friendlyCaptchaApplicationkey
    : undefined;

  const formStructure = createFormStructure({
    fields: inputFields,
    friendlyCaptchaApplicationKey,
    getLabel,
    formId: formData.formHeader.form.form_id,
  });
  const flattenedFormStructure = flattenFormStructure({
    formStructure,
    isVisible: (field) => isFieldVisible(field, formValues, formData),
  });
  const formSchema = generateSchema(flattenedFormStructure, getLabel);
  const router = useRouter();
  const formRef = useRef<HTMLFormElement>(null);
  const fileRefs = useRef<Record<string, File | null>>({});
  const sendButtonLabel = isPerPostSelected(inputFields, formValues)
    ? getLabel("form_paid_post_order_button_label", paidPostOrderButtonLabel)
    : formData.formHeader.form.send_button_label;

  const checkErrors = () => {
    const { flattenedErrors } = safeParseSchema(formSchema, formValues);

    const hasCaptcha = flattenedFormStructure.find(
      (el) => el.type === "captcha",
    );
    const combinedErrors = {
      ...flattenedErrors,
      ...(hasCaptcha &&
        !captchaSolution && {
          [hasCaptcha.name]: [getLabel("forms_captcha_missing")],
        }),
    };
    return Object.keys(combinedErrors).length > 0 ? combinedErrors : null;
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = checkErrors();
    if (!errors) {
      setFormState("loading");
      setErrors({});

      const body: FormsRequest = {
        formId: formData.formId,
        captchaSolution,
        defaultLocale: router.defaultLocale,
        locale: router.locale,
        formValues,
        url: window.location.hostname + window.location.pathname,
        origin: window.location.hostname,
      };

      const response = await fetch("/api/forms", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (response.status === 200) {
        if (formData.formHeader.form.erfolg.context === "redirect") {
          const redirectLink = has(formData.formHeader.form.erfolg, "link")
            ? (formData.formHeader.form.erfolg.link as string)
            : "";
          if (!redirectLink) {
            console.warn("Redirect link is empty");
            return;
          }
          const optimizedRedirectLink = optimizeURL(
            redirectLink,
            isNobleElements,
          );
          if (!optimizedRedirectLink) {
            return;
          }
          router.push(optimizedRedirectLink);
        } else {
          setFormState("success");
        }
      } else {
        setFormState("error");
      }
    } else {
      const firstErrorField = formRef.current?.querySelector(
        `[data-field-name=${Object.keys(errors)[0]}]`,
      );

      if (firstErrorField) {
        firstErrorField.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }

      setErrors(errors);
    }
  };

  const handleCaptchaComplete = useCallback(
    (solution: string) => {
      setCaptchaSolution(solution);
    },
    [setCaptchaSolution],
  );

  const FormJSX = (
    <form
      onSubmit={onSubmit}
      noValidate
      key={"form"}
      ref={formRef}
      autoComplete="on"
      className={`${styles["form-builder"]}`}
    >
      <div className="is-relative ">
        <div className="columns is-multiline mb-0">
          {renderFields({
            fields: formStructure,
            setFormValues,
            errors,
            friendlyCaptchaApplicationKey,
            formValues,
            handleCaptchaComplete,
            formState,
            formData,
            requiredLabel: getLabel("form_required_label"),
            fileRefs,
          })}
        </div>
        {formState === "loading" && (
          <Loader>
            <FormMessages
              state={formState}
              setformState={setFormState}
              obj={{
                context: formData.formHeader.form.loading.context,
                title: formData.formHeader.form.loading.title,
              }}
            />
          </Loader>
        )}
      </div>

      <SimpleButton
        btnType={button_type}
        text={sendButtonLabel}
        type="submit"
        disabled={formState !== "init"}
      ></SimpleButton>

      {formData.formHeader.form.refill_form_button_label && (
        <button
          type="button"
          className={`${styles["refill-form-link"]} button-reset`}
          onClick={(e) => {
            e.preventDefault();
            setErrors({});
            setFormValues({});
            fileRefs.current = {};

            //for partially filled date fields
            formRef.current
              ?.querySelectorAll("input[type=date]")
              .forEach((el) => {
                const input = el as HTMLInputElement;
                input.value = "";
              });
          }}
          disabled={formState !== "init"}
        >
          {formData.formHeader.form.refill_form_button_label}
        </button>
      )}
    </form>
  );

  useEffect(() => {
    if (Object.keys(errors ?? {}).length > 0) {
      const { data, flattenedErrors } = safeParseSchema(formSchema, formValues);
      if (!data) {
        setErrors({ ...flattenedErrors });
      } else {
        setErrors({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues]);

  return (
    <>
      {(formState === "init" || formState === "loading") && FormJSX}
      {(formState === "success" || formState === "error") && (
        <FormMessages
          state={formState}
          setformState={setFormState}
          obj={{
            context:
              formState === "success"
                ? formData.formHeader.form.erfolg.context
                : formData.formHeader.form.error.context,
            title:
              formState === "success"
                ? formData.formHeader.form.erfolg.title
                : formData.formHeader.form.error.title,
          }}
        />
      )}
    </>
  );
};

export default Form;
